import api from '@/lib/axios';
import { Password } from '@/features/passwords/services/passwords.service';
import type {
  DailyTask,
  TaskDependency,
} from '@/features/daily-tasks/services/daily-tasks.service';

export type TechSystemMemberLevel = 'viewer' | 'manager';

export interface TechSystem {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  clientId?: string | null;
  clientName?: string | null;
  isInternal: boolean;
  systemType: string;
  status: string;
  tags?: string[];
  techStack?: Record<string, string>;
  notebookLmUrl?: string | null;
  createdAt: string;
  updatedAt: string;
  members?: TechSystemMember[];
}

export interface TechSystemMember {
  id: string;
  userId: string;
  accessLevel: TechSystemMemberLevel;
  userEmail?: string;
  grantedAt: string;
}

export interface TechSystemDocPageSummary {
  id: string;
  title: string;
  slug: string;
  sortOrder: number;
  isPublished: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TechSystemDocPage extends TechSystemDocPageSummary {
  contentHtml?: string | null;
  parentId?: string | null;
  mindmapData?: unknown;
}

export interface TechSystemDocsListResponse {
  items: TechSystemDocPageSummary[];
  total: number;
  page: number;
  limit: number;
}

export interface TechSystemMindmapData {
  nodes: unknown[];
  edges: unknown[];
}

export interface TechSystemPasswordsListResponse {
  items: Password[];
  total: number;
  page: number;
  limit: number;
}

export interface TechSystemTicket {
  id: string;
  tenantId: string;
  techSystemId: string;
  clientId?: string | null;
  title: string;
  status: string;
  priority: string;
  createdBy?: string | null;
  assignedTo?: string | null;
  hoursSpent?: string | null;
  financialCost?: string | null;
  paymentDate?: string | null;
  billingNotes?: string | null;
  billable?: boolean;
  closedAt?: string | null;
  closedBy?: string | null;
  invoicedAt?: string | null;
  invoicedBy?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TechSystemTicketListItem {
  ticket: TechSystemTicket;
  systemName: string;
  clientName: string | null;
}

export interface TechSystemTicketsListResponse {
  items: TechSystemTicketListItem[];
  total: number;
  page: number;
  limit: number;
}

export interface TechSystemTicketAttachment {
  id: string;
  filename: string;
  mimeType?: string | null;
  createdAt: string;
  downloadUrl: string;
}

export interface TechSystemTicketMessageItem {
  message: {
    id: string;
    ticketId: string;
    authorId: string;
    bodyHtml: string;
    parentId?: string | null;
    createdAt: string;
  };
  authorEmail: string | null;
  authorName: string | null;
  attachments: TechSystemTicketAttachment[];
}

export interface TechSystemTicketDetail extends TechSystemTicket {
  systemName: string;
  clientName: string | null;
  canMutate?: boolean;
  messages: TechSystemTicketMessageItem[];
}

export interface CloseTechSystemTicketDto {
  hoursSpent?: number;
  financialCost?: number;
  paymentDate?: string;
  billingNotes?: string;
  billable?: boolean;
}

export type PingHttpMethod = 'GET' | 'POST';

export interface TechSystemPingEndpoint {
  id: string;
  techSystemId: string;
  name: string;
  url: string;
  method: PingHttpMethod | string;
  headers?: Record<string, string>;
  body?: string | null;
  expectedStatusCodes?: number[];
  timeoutMs: number;
  failureThreshold: number;
  isEnabled: boolean;
  lastStatus?: string | null;
  lastCheckedAt?: string | null;
  consecutiveFailures?: number;
  createdAt: string;
  updatedAt: string;
}

export interface TechSystemPingRunResult {
  status: string;
  responseTimeMs?: number;
  statusCode?: number;
  errorMessage?: string;
  checkedAt: string;
  endpoint: TechSystemPingEndpoint;
}

export interface TechSystemPingStats {
  uptimePercent: number;
  total: number;
  results: Array<{
    id: string;
    status: string;
    responseTimeMs?: number | null;
    statusCode?: number | null;
    errorMessage?: string | null;
    checkedAt: string;
  }>;
}

export interface TechSystemCodeLink {
  id: string;
  techSystemId: string;
  title: string;
  url: string;
  description?: string | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface TechSystemBackup {
  id: string;
  tenantId: string;
  techSystemId: string;
  name: string;
  driveUrl: string;
  backupDate: string;
  description?: string | null;
  createdBy?: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
}

export interface TechSystemBackupListItem {
  backup: TechSystemBackup;
  createdByEmail: string | null;
}

export interface TechSystemBackupsListResponse {
  items: TechSystemBackupListItem[];
  total: number;
  page: number;
  limit: number;
}

export interface TechSystemBackupSettings {
  id: string;
  techSystemId: string;
  mainFolderUrl?: string | null;
  recurrenceValue: number;
  recurrenceUnit: 'day' | 'week' | 'month';
  notifyUserIds: string[];
  notifyVia: Array<'asseto' | 'wave'>;
  lastAlertedAt?: string | null;
  isEnabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TechSystemProjectTaskFilters {
  assigneeIds?: string[];
  tagIds?: string[];
  workflowStatus?: string;
  priority?: string;
  search?: string;
  /** Filtra pelas tarefas de um projeto (instância de template). */
  projectInstanceId?: string;
  page?: number;
  /** Máximo aceito pelo backend: 1000. */
  limit?: number;
}

export interface TechSystemPingEndpointPayload {
  name: string;
  url: string;
  method?: PingHttpMethod;
  headers?: Record<string, string>;
  body?: string | null;
  expectedStatusCodes?: number[];
  timeoutMs?: number;
  failureThreshold?: number;
  isEnabled?: boolean;
}

export const techSystemsService = {
  list: (params?: Record<string, string>) =>
    api.get<TechSystem[]>('/tech-systems', { params }).then((r) => r.data),

  listForProjectsHub: (params?: Record<string, string>) =>
    api
      .get<TechSystem[]>('/tech-systems/projects/systems', { params })
      .then((r) => r.data),

  get: (id: string) =>
    api.get<TechSystem>(`/tech-systems/${id}`).then((r) => r.data),

  create: (data: Partial<TechSystem>) =>
    api.post<TechSystem>('/tech-systems', data).then((r) => r.data),

  update: (id: string, data: Partial<TechSystem>) =>
    api.patch<TechSystem>(`/tech-systems/${id}`, data).then((r) => r.data),

  remove: (id: string) =>
    api.delete(`/tech-systems/${id}`).then((r) => r.data),

  listMembers: (id: string) =>
    api.get<TechSystemMember[]>(`/tech-systems/${id}/members`).then((r) => r.data),

  addMember: (id: string, userId: string, accessLevel: TechSystemMemberLevel) =>
    api
      .post(`/tech-systems/${id}/members`, { userId, accessLevel })
      .then((r) => r.data),

  removeMember: (id: string, userId: string) =>
    api.delete(`/tech-systems/${id}/members/${userId}`).then((r) => r.data),

  listPasswords: (
    id: string,
    params?: { search?: string; page?: number; limit?: number },
  ) =>
    api
      .get<TechSystemPasswordsListResponse>(`/tech-systems/${id}/passwords`, {
        params,
      })
      .then((r) => r.data),

  listProjectDependencies: (id: string) =>
    api
      .get<TaskDependency[]>(`/tech-systems/${id}/projects/dependencies`)
      .then((r) => r.data),

  listProjectTasks: (
    id: string,
    filters?: TechSystemProjectTaskFilters,
  ) => {
    const params: Record<string, string> = {};
    if (filters?.assigneeIds?.length) {
      params.assigneeIds = filters.assigneeIds.join(',');
    }
    if (filters?.tagIds?.length) {
      params.tagIds = filters.tagIds.join(',');
    }
    if (filters?.workflowStatus) params.workflowStatus = filters.workflowStatus;
    if (filters?.priority) params.priority = filters.priority;
    if (filters?.search) params.search = filters.search;
    if (filters?.projectInstanceId) {
      params.projectInstanceId = filters.projectInstanceId;
    }
    if (filters?.page) params.page = String(filters.page);
    if (filters?.limit) params.limit = String(filters.limit);

    return api
      .get<DailyTask[]>(`/tech-systems/${id}/projects/tasks`, { params })
      .then((r) => r.data);
  },

  listInfra: (id: string) =>
    api.get(`/tech-systems/${id}/infra-items`).then((r) => r.data),

  listBackups: (
    id: string,
    params?: { search?: string; page?: number; limit?: number },
  ) =>
    api
      .get<TechSystemBackupsListResponse>(`/tech-systems/${id}/backups`, {
        params,
      })
      .then((r) => r.data),

  createBackup: (
    id: string,
    data: {
      name: string;
      driveUrl: string;
      backupDate: string;
      description?: string;
    },
  ) => api.post<TechSystemBackup>(`/tech-systems/${id}/backups`, data).then((r) => r.data),

  updateBackup: (
    id: string,
    backupId: string,
    data: Partial<{
      name: string;
      driveUrl: string;
      backupDate: string;
      description: string | null;
    }>,
  ) =>
    api
      .patch<TechSystemBackup>(`/tech-systems/${id}/backups/${backupId}`, data)
      .then((r) => r.data),

  deleteBackup: (id: string, backupId: string) =>
    api.delete(`/tech-systems/${id}/backups/${backupId}`).then((r) => r.data),

  getBackupSettings: (id: string) =>
    api
      .get<TechSystemBackupSettings>(`/tech-systems/${id}/backup-settings`)
      .then((r) => r.data),

  updateBackupSettings: (
    id: string,
    data: Partial<{
      mainFolderUrl: string | null;
      recurrenceValue: number;
      recurrenceUnit: 'day' | 'week' | 'month';
      notifyUserIds: string[];
      notifyVia: Array<'asseto' | 'wave'>;
      isEnabled: boolean;
      lastAlertedAt: string | null;
    }>,
  ) =>
    api
      .patch<TechSystemBackupSettings>(`/tech-systems/${id}/backup-settings`, data)
      .then((r) => r.data),

  listCodeLinks: (id: string) =>
    api
      .get<TechSystemCodeLink[]>(`/tech-systems/${id}/code-links`)
      .then((r) => r.data),

  createCodeLink: (
    id: string,
    data: { title: string; url: string; description?: string; sortOrder?: number },
  ) =>
    api
      .post<TechSystemCodeLink>(`/tech-systems/${id}/code-links`, data)
      .then((r) => r.data),

  updateCodeLink: (
    id: string,
    linkId: string,
    data: Partial<{
      title: string;
      url: string;
      description: string | null;
      sortOrder: number;
    }>,
  ) =>
    api
      .patch<TechSystemCodeLink>(
        `/tech-systems/${id}/code-links/${linkId}`,
        data,
      )
      .then((r) => r.data),

  deleteCodeLink: (id: string, linkId: string) =>
    api
      .delete(`/tech-systems/${id}/code-links/${linkId}`)
      .then((r) => r.data),

  listDocs: (id: string, params?: Record<string, string>) =>
    api
      .get<TechSystemDocsListResponse>(`/tech-systems/${id}/docs`, { params })
      .then((r) => r.data),

  getDoc: (id: string, pageId: string) =>
    api
      .get<TechSystemDocPage>(`/tech-systems/${id}/docs/${pageId}`)
      .then((r) => r.data),

  saveDoc: (id: string, data: Record<string, unknown>) =>
    api.post<TechSystemDocPage>(`/tech-systems/${id}/docs`, data).then((r) => r.data),

  deleteDoc: (id: string, pageId: string) =>
    api
      .delete(`/tech-systems/${id}/docs/${pageId}`)
      .then((r) => r.data),

  getMindmap: (id: string) =>
    api
      .get<{ mindmapData: TechSystemMindmapData }>(`/tech-systems/${id}/mindmap`)
      .then((r) => r.data),

  saveMindmap: (id: string, mindmapData: TechSystemMindmapData) =>
    api
      .put<{ mindmapData: TechSystemMindmapData }>(`/tech-systems/${id}/mindmap`, {
        mindmapData,
      })
      .then((r) => r.data),

  listPingEndpoints: (id: string) =>
    api
      .get<TechSystemPingEndpoint[]>(`/tech-systems/${id}/ping-endpoints`)
      .then((r) => r.data),

  createPingEndpoint: (id: string, data: TechSystemPingEndpointPayload) =>
    api
      .post<TechSystemPingEndpoint>(`/tech-systems/${id}/ping-endpoints`, data)
      .then((r) => r.data),

  updatePingEndpoint: (
    id: string,
    endpointId: string,
    data: Partial<TechSystemPingEndpointPayload>,
  ) =>
    api
      .patch<TechSystemPingEndpoint>(
        `/tech-systems/${id}/ping-endpoints/${endpointId}`,
        data,
      )
      .then((r) => r.data),

  deletePingEndpoint: (id: string, endpointId: string) =>
    api
      .delete(`/tech-systems/${id}/ping-endpoints/${endpointId}`)
      .then((r) => r.data),

  pingStats: (id: string, endpointId: string, days = 7) =>
    api
      .get<TechSystemPingStats>(
        `/tech-systems/${id}/ping-endpoints/${endpointId}/stats`,
        { params: { days } },
      )
      .then((r) => r.data),

  runPingNow: (id: string, endpointId: string) =>
    api
      .post<TechSystemPingRunResult>(
        `/tech-systems/${id}/ping-endpoints/${endpointId}/run`,
      )
      .then((r) => r.data),

  listGithubRepos: (id: string) =>
    api.get(`/tech-systems/${id}/github-repos`).then((r) => r.data),

  githubTree: (repoId: string, path = '') =>
    api
      .get(`/tech-systems/github-repos/${repoId}/tree`, { params: { path } })
      .then((r) => r.data),

  githubFile: (repoId: string, path: string) =>
    api
      .get(`/tech-systems/github-repos/${repoId}/file`, { params: { path } })
      .then((r) => r.data),

  listTickets: (params?: {
    techSystemId?: string;
    status?: string;
    search?: string;
    page?: number;
    limit?: number;
  }) =>
    api
      .get<TechSystemTicketsListResponse>('/tech-systems/tickets', { params })
      .then((r) => r.data),

  listFinanceTickets: (params?: {
    search?: string;
    page?: number;
    limit?: number;
    invoiced?: 'pending' | 'invoiced' | 'all';
  }) =>
    api
      .get<TechSystemTicketsListResponse>('/tech-systems/tickets/finance', {
        params,
      })
      .then((r) => r.data),

  getFinanceDashboard: (month?: string) =>
    api
      .get<{
        month: string;
        totals: {
          pendingAmount: number;
          pendingCount: number;
          invoicedAmount: number;
          invoicedCount: number;
          monthInvoicedAmount: number;
          monthInvoicedCount: number;
        };
        daily: Array<{ date: string; amount: number; count: number }>;
        byMonth: Array<{ month: string; amount: number; count: number }>;
        byClient: {
          monthInvoiced: Array<{
            clientId: string | null;
            clientName: string;
            amount: number;
            count: number;
          }>;
          allInvoiced: Array<{
            clientId: string | null;
            clientName: string;
            amount: number;
            count: number;
          }>;
          pending: Array<{
            clientId: string | null;
            clientName: string;
            amount: number;
            count: number;
          }>;
        };
      }>('/tech-systems/tickets/finance/dashboard', { params: { month } })
      .then((r) => r.data),

  markTicketInvoiced: (ticketId: string) =>
    api
      .patch<TechSystemTicket>(`/tech-systems/tickets/${ticketId}/invoice`)
      .then((r) => r.data),

  uninvoiceTicket: (ticketId: string) =>
    api
      .patch<TechSystemTicket>(`/tech-systems/tickets/${ticketId}/uninvoice`)
      .then((r) => r.data),

  getTicket: (id: string) =>
    api
      .get<TechSystemTicketDetail>(`/tech-systems/tickets/${id}`)
      .then((r) => r.data),

  createTicket: (data: {
    techSystemId: string;
    title: string;
    priority?: string;
    assignedTo?: string;
  }) => api.post<TechSystemTicket>('/tech-systems/tickets', data).then((r) => r.data),

  addTicketMessage: (id: string, bodyHtml: string, parentId?: string) =>
    api
      .post(`/tech-systems/tickets/${id}/messages`, { bodyHtml, parentId })
      .then((r) => r.data),

  uploadTicketAttachment: (
    ticketId: string,
    messageId: string,
    file: File,
  ) => {
    const form = new FormData();
    form.append('file', file);
    return api
      .post(
        `/tech-systems/tickets/${ticketId}/messages/${messageId}/attachments`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      )
      .then((r) => r.data);
  },

  closeTicket: (id: string, data: CloseTechSystemTicketDto) =>
    api
      .patch<TechSystemTicket>(`/tech-systems/tickets/${id}/close`, data)
      .then((r) => r.data),

  reopenTicket: (id: string) =>
    api
      .patch<TechSystemTicket>(`/tech-systems/tickets/${id}/reopen`)
      .then((r) => r.data),

  getConfig: () =>
    api.get('/tech-systems/config/settings').then((r) => r.data),

  updateConfig: (data: Record<string, unknown>) =>
    api.patch('/tech-systems/config/settings', data).then((r) => r.data),

  listFinanceUsers: () =>
    api.get('/tech-systems/config/finance-users').then((r) => r.data),

  addFinanceUser: (userId: string) =>
    api.post('/tech-systems/config/finance-users', { userId }).then((r) => r.data),

  removeFinanceUser: (userId: string) =>
    api.delete(`/tech-systems/config/finance-users/${userId}`).then((r) => r.data),

  listGithubTokens: () =>
    api.get('/tech-systems/config/github-tokens').then((r) => r.data),

  createGithubToken: (data: { label: string; token: string; scopesNote?: string }) =>
    api.post('/tech-systems/config/github-tokens', data).then((r) => r.data),
};
